import React, { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Loader2, Check, AlertCircle, MessageCircle } from "lucide-react";
import { COMPANY, INDUSTRIES } from "../lib/constants";
import { trackLead, trackContact, getFbCookies } from "../lib/pixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Strategy-session modal — shared form for ALL primary CTAs.
 * Fields: full_name, company_name, industry, email, phone, help_request.
 * Below the form: prominent WhatsApp fallback CTA.
 */
export default function StrategyModal({ open, onClose, source = "home", defaults = {} }) {
  const [form, setForm] = useState({
    full_name: "", company_name: "", industry: "", email: "", phone: "",
    help_request: "", website: "",
  });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  // close on esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // reset when opened fresh — apply any defaults (e.g., pre-filled industry)
  useEffect(() => {
    if (open) {
      setState("idle");
      setMsg("");
      setErrors({});
      setForm((p) => ({
        ...p,
        industry: defaults.industry || p.industry || "",
        help_request: defaults.help_request || p.help_request || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) e.full_name = "Please enter your name";
    if (!form.company_name.trim()) e.company_name = "Please enter your company name";
    if (!form.industry) e.industry = "Pick your industry";
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) e.email = "Please enter a valid work email";
    if (!form.phone.trim() || form.phone.trim().length < 6) e.phone = "Please enter a contact number";
    if (!form.help_request.trim() || form.help_request.trim().length < 5) e.help_request = "Tell us a little about what you're solving";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setState("loading");
    setMsg("");
    try {
      const { fbp, fbc } = getFbCookies();
      const event_source_url = typeof window !== "undefined" ? window.location.href : undefined;
      const res = await axios.post(`${API}/contact/submit`, {
        ...form,
        source_page: source,
        fbp,
        fbc,
        event_source_url,
      });
      if (res.status === 200 && res.data?.success) {
        setState("success");
        setMsg(res.data.message || "We'll reach out within one business day.");
        setForm({ full_name: "", company_name: "", industry: "", email: "", phone: "", help_request: "", website: "" });
        // Meta Pixel — Lead conversion fires ONLY on confirmed 200 + success=true.
        // Pass the backend-issued event_id so Meta dedupes browser vs CAPI events.
        trackLead("contact-form", res.data.event_id);
      } else throw new Error("Unexpected response");
    } catch (err) {
      setState("error");
      const detail = err?.response?.data?.detail;
      setMsg(Array.isArray(detail) ? detail[0]?.msg : detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="strategy-modal"
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-brand-ink/70 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* panel */}
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="relative w-full sm:max-w-2xl bg-brand-ink text-white rounded-t-[28px] sm:rounded-[28px] shadow-glassDark overflow-hidden border border-white/10"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-0 dot-grid-dark opacity-40 pointer-events-none" />
            <div
              className="absolute -inset-0 pointer-events-none"
              style={{ background: "radial-gradient(500px 300px at 10% 0%, rgba(61,214,140,0.20), transparent 60%)" }}
            />

            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="strategy-modal-close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/15 bg-white/5 flex items-center justify-center hover:bg-white/10 z-10"
            >
              <X size={16} />
            </button>

            <div className="relative p-6 sm:p-9 max-h-[92vh] overflow-y-auto">
              <div className="section-label" style={{ color: "rgba(255,255,255,0.55)" }}>
                Free 30-min · Strategy session
              </div>
              <h3 className="mt-3 text-2xl sm:text-3xl font-sora font-semibold tracking-tight leading-tight">
                Tell us about your <span className="text-brand-green">growth cycle.</span>
              </h3>
              <p className="mt-2 text-white/65 text-sm max-w-lg">
                One form. We reply within one business day with a first read on where you can unlock growth.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-4" noValidate data-testid="strategy-modal-form">
                {/* honeypot */}
                <input type="text" tabIndex="-1" autoComplete="off" name="website" value={form.website} onChange={set("website")} className="hidden" aria-hidden />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <MField label="Full name" error={errors.full_name}>
                    <input className="m-input" value={form.full_name} onChange={set("full_name")} placeholder="Priya Sharma" data-testid="sm-fullname" />
                  </MField>
                  <MField label="Company name" error={errors.company_name}>
                    <input className="m-input" value={form.company_name} onChange={set("company_name")} placeholder="Northwind Ltd." data-testid="sm-company" />
                  </MField>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <MField label="Email" error={errors.email}>
                    <input type="email" className="m-input" value={form.email} onChange={set("email")} placeholder="priya@company.com" data-testid="sm-email" />
                  </MField>
                  <MField label="Contact number" error={errors.phone}>
                    <input type="tel" className="m-input" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" data-testid="sm-phone" />
                  </MField>
                </div>

                <MField label="Industry" error={errors.industry}>
                  <select className="m-input appearance-none" value={form.industry} onChange={set("industry")} data-testid="sm-industry">
                    <option value="" className="bg-brand-ink">Select your industry…</option>
                    {INDUSTRIES.filter((i) => i !== "& more").map((i) => (
                      <option key={i} value={i} className="bg-brand-ink">{i}</option>
                    ))}
                    <option value="Other" className="bg-brand-ink">Other</option>
                  </select>
                </MField>

                <MField label="How can we help?" error={errors.help_request}>
                  <textarea rows={3} className="m-input resize-none" value={form.help_request} onChange={set("help_request")}
                    placeholder="A bit about where you are, what's stuck, and the outcome you're chasing…" data-testid="sm-message" />
                </MField>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                  <button type="submit" disabled={state === "loading"}
                    className="ss-btn-primary !py-3 !px-6 !text-[14px] disabled:opacity-60" data-testid="sm-submit">
                    {state === "loading" ? <><Loader2 size={15} className="animate-spin" /> Sending…</>
                      : state === "success" ? <><Check size={15} /> Sent</>
                      : <>Send request <Send size={14} /></>}
                  </button>
                  {msg && (
                    <p className={`flex items-center gap-2 text-sm ${state === "success" ? "text-brand-green" : "text-red-400"}`} data-testid="sm-feedback">
                      {state === "success" ? <Check size={13} /> : <AlertCircle size={13} />}
                      {msg}
                    </p>
                  )}
                </div>
              </form>

              {/* Divider */}
              <div className="mt-8 flex items-center gap-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs uppercase tracking-[0.25em] text-white/45">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* WhatsApp fallback */}
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackContact("strategy-modal-whatsapp")}
                className="mt-5 w-full inline-flex items-center justify-center gap-3 py-3.5 rounded-full bg-white/5 border border-white/12 hover:bg-brand-green hover:text-brand-ink hover:border-brand-green transition-colors font-medium text-[14px]"
                data-testid="sm-whatsapp"
              >
                <MessageCircle size={16} />
                Reach out on WhatsApp instead
              </a>
            </div>

            <style>{`
              .m-input {
                width: 100%;
                background: rgba(255,255,255,0.04);
                border: 1px solid rgba(255,255,255,0.12);
                color: #fff;
                padding: 12px 14px;
                border-radius: 12px;
                font-size: 14px;
                outline: none;
                transition: border-color .2s, background .2s, box-shadow .2s;
              }
              .m-input::placeholder { color: rgba(255,255,255,0.38); }
              .m-input:focus { border-color: rgba(61,214,140,0.65); box-shadow: 0 0 0 3px rgba(61,214,140,0.15); background: rgba(255,255,255,0.06); }
              .m-input option { color: #fff; }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MField({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-1.5">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-400">{error}</span>}
    </label>
  );
}
